import React, { useMemo, useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link, Typography } from '@mui/material'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { useSnackbar } from 'notistack'
import { Pill, SectionCard } from './common'
import { openS3Doc, exportPreviewPdf } from '../s3Doc'
import DataTable from './Tables'
import {
  downloadXlsx, downloadSheets, allocationBdRows, allocationInternalRows, serviceabilityRows, marginWorkingRows, exceptionRows,
  rateCardMarginPreviewRows, downloadRateCardMarginWorking,
} from './fileExports'

/**
 * WorkingFilesCard — the working pack shown in a deal review (L1/FH).
 * Every file is built from REAL version data (computedLanes) — reviewers see
 * cost/margin (the backend masks those only for SL). The Client Proposal is the
 * real S3 doc. `onCounterUi` / `onUploadCounter` wire the Client Proposal actions.
 * rtoLanes (computedRtoLanes, D2C RTO Margin Breakout §5) adds a second "Margin
 * Working (RTO)" tab to the SAME Margin Working file when this deal has any RTO lanes.
 */
// bare: render just the file grid + dialog (with a small inner heading), no SectionCard
// wrapper — for callers that fold the files into their own section (DealReview's "Trail").
export default function WorkingFilesCard({ versionLabel = 'current', lanes = [], rtoLanes = [], docUri, versionId, brand = 'proposal', onCounterUi, onUploadCounter, bare = false }) {
  const { enqueueSnackbar } = useSnackbar()
  const safe = brand.replace(/[^\w-]+/g, '_')
  const files = useMemo(() => ([
    { ttl: 'Client Proposal', sub: 'Outward rate cards + T&Cs — D2C format', tag: 'SL✔', kind: 'doc' },
    {
      ttl: 'Internal Margin Working', sub: 'Courier-wise revenue / cost / margin per lane', tag: 'Pricing / FH only',
      rows: marginWorkingRows(lanes), file: `${safe}_Margin_Working.xlsx`, sheet: 'Margin Working',
      ...(rtoLanes.length ? { extraSheets: [{ name: 'Margin Working (RTO)', rows: marginWorkingRows(rtoLanes) }] } : {}),
    },
    { ttl: 'Approximate allocation', sub: 'Courier parent × service × zone (no CP codes)', tag: 'SL✔', rows: allocationBdRows(lanes), file: `${safe}_Approximate_Allocation.xlsx`, sheet: 'Approximate Allocation' },
    { ttl: 'Allocation — internal view', sub: 'Full CP account codes × priority × allocation %', tag: 'Pricing / FH only', rows: allocationInternalRows(lanes), file: `${safe}_Allocation_Internal.xlsx`, sheet: 'Allocation Internal' },
    { ttl: 'Serviceability and TAT', sub: 'Zone × weight slab serviceability + transit days', tag: 'SL✔', rows: serviceabilityRows(lanes), file: `${safe}_Serviceability_TAT.xlsx`, sheet: 'Serviceability' },
    { ttl: 'Exception & risk report', sub: 'Guardrail-flagged lanes', tag: 'Pricing / FH only', rows: exceptionRows(lanes), file: `${safe}_Exceptions.xlsx`, sheet: 'Exceptions' },
    {
      ttl: 'Rate Card Margin Working', sub: 'Margin Working — RTO / Prepaid / COD tabs; per-zone margin & rates. "Final Rate" is editable — margin recalculates in Excel.', tag: 'Pricing / FH only',
      pivot: true, previewRows: rateCardMarginPreviewRows(lanes), file: `${safe}_Rate_Card_Margin_Working.xlsx`,
    },
  ]), [lanes, rtoLanes, safe])
  const [preview, setPreview] = useState(null)
  const previewCols = (rows) => (rows[0] ? Object.keys(rows[0]).map((k) => ({ key: k, label: k })) : [])
  const Wrapper = bare ? Box : SectionCard

  return (
    <Wrapper {...(bare ? {} : { title: `Working files — ${versionLabel}`, subtitle: 'Any change in the rate card automatically recalculates every other working file. Previous versions stay in the thread side-by-side.' })}>
      {bare ? (
        /* One row per file, styled as a real downloadable document: tinted file-type
           icon + name + extension, visibility pill, and icon buttons for Preview /
           Download — the affordance says "get your files here" at a glance. */
        <Box>
          {files.map((f) => {
            const shareable = f.tag === 'SL✔'
            const isDoc = f.kind === 'doc'
            const empty = !isDoc && (f.pivot ? !f.previewRows.length : !f.rows.length)
            return (
              <Box key={f.ttl} title={f.sub} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: '8px', px: 1.5, py: 1, mb: 1, '&:last-of-type': { mb: 0 }, '&:hover': { bgcolor: '#FAFBFC', borderColor: '#C7D0E0' } }}>
                <Box sx={{ width: 34, height: 34, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: isDoc ? '#EAF1FC' : '#ECFDF5' }}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 19, color: isDoc ? '#2065D1' : '#15803D' }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', lineHeight: 1.3 }}>{f.ttl}</Typography>
                  <Typography sx={{ fontSize: 10.5, color: 'text.disabled', lineHeight: 1.3 }}>{isDoc ? 'PDF' : '.xlsx'}</Typography>
                </Box>
                <Pill label={f.tag} styleKey={shareable ? 'approved' : 'purple'} />
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', ml: 'auto', flexWrap: 'wrap' }}>
                  {isDoc ? (
                    <>
                      {docUri
                        ? <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon />} sx={fileBtn} onClick={() => openS3Doc(docUri, (m) => enqueueSnackbar(m, { variant: 'error' }))}>Open</Button>
                        : (
                          <Box component="span" title="Generated fresh from this version's lanes">
                            <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon />} sx={fileBtn} disabled={!lanes.length} onClick={() => exportPreviewPdf(versionId, (m) => enqueueSnackbar(m, { variant: 'error' }))}>Preview PDF</Button>
                          </Box>
                        )}
                      {docUri && !!lanes.length && <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon />} sx={fileBtn} onClick={() => exportPreviewPdf(versionId, (m) => enqueueSnackbar(m, { variant: 'error' }))}>Export preview</Button>}
                      <Button size="small" variant="outlined" startIcon={<UploadFileOutlinedIcon />} sx={fileBtn} onClick={() => onUploadCounter && onUploadCounter()}>Upload counter</Button>
                      <Button size="small" variant="contained" sx={fileBtn} onClick={() => onCounterUi && onCounterUi()}>Create Counter in UI</Button>
                    </>
                  ) : (
                    <>
                      <Box component="span" title={empty ? 'No priced lanes on this version yet' : undefined}>
                        <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon />} sx={fileBtn} disabled={empty} onClick={() => setPreview({ title: f.ttl, rows: f.pivot ? f.previewRows : f.rows, pivot: f.pivot, file: f.file })}>Preview</Button>
                      </Box>
                      <Box component="span" title={empty ? 'No priced lanes on this version yet' : undefined}>
                        <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} sx={fileBtn} disabled={empty} onClick={() => (f.pivot
                          ? downloadRateCardMarginWorking(f.file, lanes, rtoLanes)
                          : f.extraSheets?.length
                            ? downloadSheets(f.file, [{ name: f.sheet, rows: f.rows }, ...f.extraSheets])
                            : downloadXlsx(f.file, f.rows, f.sheet))}>Download</Button>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )
          })}
        </Box>
      ) : (
      <Grid container spacing={1.5}>
        {files.map((f) => {
          const shareable = f.tag === 'SL✔'
          const isDoc = f.kind === 'doc'
          const empty = !isDoc && (f.pivot ? !f.previewRows.length : !f.rows.length)
          return (
            <Grid item xs={12} md={6} key={f.ttl}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '9px', p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 12, flex: 1 }}>{f.ttl}</Typography>
                  <Pill label={f.tag} styleKey={shareable ? 'approved' : 'purple'} />
                </Box>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5, flex: 1 }}>{f.sub}</Typography>
                <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {isDoc ? (
                    <>
                      {docUri
                        ? <Link component="button" underline="hover" sx={{ fontSize: 12, fontWeight: 600 }} onClick={() => openS3Doc(docUri, (m) => enqueueSnackbar(m, { variant: 'error' }))}>👁 Preview / ⬇ Download</Link>
                        : <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>PDF not generated yet</Typography>}
                      {/* Export Preview (FE Integration Guide v3 §1) — safe at any status, no
                          side effects; unlike the link above, this doesn't need Share/APPROVED. */}
                      <Button size="small" variant="outlined" sx={btnXs} disabled={!lanes.length}
                        onClick={() => exportPreviewPdf(versionId, (m) => enqueueSnackbar(m, { variant: 'error' }))}>
                        🔍 Export / Preview
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="small" variant="outlined" sx={btnXs} disabled={empty} onClick={() => setPreview({ title: f.ttl, rows: f.pivot ? f.previewRows : f.rows, pivot: f.pivot, file: f.file })}>👁 Preview</Button>
                      <Button size="small" variant="outlined" sx={btnXs} disabled={empty}
                        onClick={() => (f.pivot
                          ? downloadRateCardMarginWorking(f.file, lanes, rtoLanes)
                          : f.extraSheets?.length
                            ? downloadSheets(f.file, [{ name: f.sheet, rows: f.rows }, ...f.extraSheets])
                            : downloadXlsx(f.file, f.rows, f.sheet))}>⬇ Download</Button>
                      {empty && <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>no lanes</Typography>}
                    </>
                  )}
                  {isDoc && (
                    <>
                      <Button size="small" variant="outlined" sx={btnXs} onClick={() => onUploadCounter && onUploadCounter()}>Upload counter</Button>
                      <Button size="small" variant="contained" sx={btnXs} onClick={() => onCounterUi && onCounterUi()}>Create Counter in UI</Button>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>
          )
        })}
      </Grid>
      )}

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>{preview?.title} — preview</DialogTitle>
        <DialogContent dividers>
          {preview && (preview.rows.length
            ? <DataTable columns={previewCols(preview.rows)} rows={preview.rows.map((r, i) => ({ ...r, _k: i }))} getRowKey={(r) => r._k} />
            : <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No rows to preview.</Typography>)}
        </DialogContent>
        <DialogActions>
          {preview && !!preview.rows.length && (
            <Button variant="outlined" sx={btnXs} onClick={() => (preview.pivot
              ? downloadRateCardMarginWorking(preview.file, lanes, rtoLanes)
              : downloadXlsx(`${safe}_${preview.title.replace(/[^\w-]+/g, '_')}.xlsx`, preview.rows, preview.title.slice(0, 28)))}>⬇ Download .xlsx</Button>
          )}
          <Button variant="text" sx={btnXs} onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  )
}

const btnXs = { fontSize: 12, py: 0.3, px: 1, minWidth: 0, textTransform: 'none', borderRadius: '6px' }
const fileBtn = { fontSize: 12, fontWeight: 700, py: 0.4, px: 1.25, minWidth: 0, textTransform: 'none', borderRadius: '7px', whiteSpace: 'nowrap', '& .MuiButton-startIcon': { mr: 0.5, '& svg': { fontSize: 15 } } }
